"use client";

import { useEffect, useMemo, useState } from "react";
import type { RadarRevealAnimationResult } from "./types";

const TOTAL_MS = 1400;

function easeOutCubic(value: number): number {
  return 1 - (1 - value) ** 3;
}

function phase(progress: number, startMs: number, endMs: number): number {
  if (progress <= startMs) {
    return 0;
  }
  if (progress >= endMs) {
    return 1;
  }
  return (progress - startMs) / (endMs - startMs);
}

function buildStaticState(
  total: number,
  control: number,
  safety: number,
  growth: number,
): RadarRevealAnimationResult {
  return {
    baseOpacity: 1,
    polygonProgress: 1,
    displayTotal: total,
    axisLabelOpacity: 1,
    showDataPoints: true,
    barWidths: { control, safety, growth },
    barLabelValues: { control, safety, growth },
    ctaOpacity: 1,
  };
}

export function useRadarRevealAnimation(options: {
  total: number;
  control: number;
  safety: number;
  growth: number;
  enabled: boolean;
}): RadarRevealAnimationResult {
  const { total, control, safety, growth, enabled } = options;
  const [elapsedMs, setElapsedMs] = useState(enabled ? 0 : TOTAL_MS);

  useEffect(() => {
    if (!enabled) {
      setElapsedMs(TOTAL_MS);
      return;
    }

    setElapsedMs(0);
    const startedAt = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const next = Math.min(now - startedAt, TOTAL_MS);
      setElapsedMs(next);
      if (next < TOTAL_MS) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [enabled, total, control, safety, growth]);

  return useMemo(() => {
    if (!enabled || elapsedMs >= TOTAL_MS) {
      return buildStaticState(total, control, safety, growth);
    }

    const baseOpacity = easeOutCubic(phase(elapsedMs, 0, 200));
    const polygonProgress = easeOutCubic(phase(elapsedMs, 200, 800));
    const axisLabelOpacity = easeOutCubic(phase(elapsedMs, 600, 900));
    const showDataPoints = elapsedMs >= 750;

    const barControl = easeOutCubic(phase(elapsedMs, 700, 1200));
    const barSafety = easeOutCubic(phase(elapsedMs, 780, 1280));
    const barGrowth = easeOutCubic(phase(elapsedMs, 860, 1360));
    const ctaOpacity = easeOutCubic(phase(elapsedMs, 1160, 1400));

    return {
      baseOpacity,
      polygonProgress,
      displayTotal: Math.round(total * polygonProgress),
      axisLabelOpacity,
      showDataPoints,
      barWidths: {
        control: control * barControl,
        safety: safety * barSafety,
        growth: growth * barGrowth,
      },
      barLabelValues: {
        control: Math.round(control * barControl),
        safety: Math.round(safety * barSafety),
        growth: Math.round(growth * barGrowth),
      },
      ctaOpacity,
    };
  }, [control, elapsedMs, enabled, growth, safety, total]);
}
